import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Save, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useDragReorder, move } from "@/admin/dnd";

const uid = () => `m-${Math.random().toString(36).slice(2, 9)}`;
const TYPE_LABEL = { link: "Link", dropdown: "Dropdown", mega: "Search mega menu" };

const Row = ({ item, onChange, onMove, onRemove, dragProps, canUp, canDown, child }) => (
  <div {...dragProps} className={`flex flex-wrap items-center gap-2 rounded-lg border bg-white p-2.5 ${child ? "ml-8" : ""}`} data-testid={`menu-item-${item.id}`}>
    <GripVertical className="h-4 w-4 text-charcoal/30 cursor-move" />
    <Input value={item.label} onChange={(e) => onChange({ ...item, label: e.target.value })} className="w-44" data-testid={`menu-label-${item.id}`} />
    {(child || item.type === "link") && <Input value={item.to} onChange={(e) => onChange({ ...item, to: e.target.value })} placeholder="/page or https://…" className="w-56 text-xs" data-testid={`menu-to-${item.id}`} />}
    {!child && <span className="text-[11px] uppercase tracking-wider text-muted-foreground px-2">{TYPE_LABEL[item.type]}</span>}
    <div className="flex items-center gap-1.5 ml-auto">
      <span className="text-xs text-muted-foreground">Show</span>
      <Switch checked={!item.hidden} onCheckedChange={(v) => onChange({ ...item, hidden: !v })} data-testid={`menu-visible-${item.id}`} />
      <button type="button" disabled={!canUp} onClick={() => onMove(-1)} className="p-1 disabled:opacity-30 hover:text-brand" aria-label="Move up" data-testid={`menu-up-${item.id}`}><ChevronUp className="h-4 w-4" /></button>
      <button type="button" disabled={!canDown} onClick={() => onMove(1)} className="p-1 disabled:opacity-30 hover:text-brand" aria-label="Move down" data-testid={`menu-down-${item.id}`}><ChevronDown className="h-4 w-4" /></button>
      {item.type !== "mega" && <button type="button" onClick={onRemove} className="p-1 hover:text-destructive" aria-label="Delete" data-testid={`menu-delete-${item.id}`}><Trash2 className="h-4 w-4" /></button>}
    </div>
  </div>
);

const Children = ({ item, update }) => {
  const kids = item.children || [];
  const setKids = (children) => update({ ...item, children });
  const drag = useDragReorder(kids, setKids);
  return (
    <div className="space-y-2 mt-2">
      {kids.map((c, j) => (
        <Row key={c.id} child item={c} dragProps={drag(j)} canUp={j > 0} canDown={j < kids.length - 1}
          onChange={(nc) => setKids(kids.map((x) => (x.id === c.id ? nc : x)))} onMove={(d) => setKids(move(kids, j, j + d))} onRemove={() => setKids(kids.filter((x) => x.id !== c.id))} />
      ))}
      <button type="button" onClick={() => setKids([...kids, { id: uid(), label: "New link", to: "/", hidden: false }])} className="ml-8 text-xs font-semibold text-brand hover:underline" data-testid={`menu-add-child-${item.id}`}>+ Add link to dropdown</button>
    </div>
  );
};

export default function MenuManager() {
  const { content, refresh } = useSite();
  const [items, setItems] = useState(content.menu.items);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setItems(content.menu.items); }, [content.menu.items]);
  const drag = useDragReorder(items, setItems);
  const update = (it) => setItems((l) => l.map((x) => (x.id === it.id ? it : x)));

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/admin/content/menu", { data: { items } });
      await refresh();
      toast.success("Menu saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-menu-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-bold text-charcoal">Menu Manager</h1><p className="text-sm text-muted-foreground">Rename, hide, reorder (drag or arrows) and add links or dropdowns.</p></div>
        <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark" data-testid="menu-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save</Button>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id}>
            <Row item={it} dragProps={drag(i)} canUp={i > 0} canDown={i < items.length - 1} onChange={update} onMove={(d) => setItems(move(items, i, i + d))} onRemove={() => setItems(items.filter((x) => x.id !== it.id))} />
            {it.type === "dropdown" && <Children item={it} update={update} />}
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={() => setItems([...items, { id: uid(), label: "New link", type: "link", to: "/", hidden: false, children: [] }])} data-testid="menu-add-link"><Plus className="h-4 w-4 mr-1" />Add link</Button>
        <Button variant="outline" onClick={() => setItems([...items, { id: uid(), label: "New dropdown", type: "dropdown", to: "", hidden: false, children: [] }])} data-testid="menu-add-dropdown"><Plus className="h-4 w-4 mr-1" />Add dropdown</Button>
      </div>
    </div>
  );
}
